import { useCallback, useEffect, useRef, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { traverse } from '@atlaskit/adf-utils/traverse/main';

import { client } from '@atlassian/rovo-content-bridge-api/client-types';
import { useRovoContentBridgeAPI } from '@atlassian/rovo-content-bridge-api/hooks';
import { InlineInputBoxSendMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import {
	RovoStreamCompletedCommand,
	RovoStreamErroredCommand,
	RovoStreamAbortedCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/streaming-lifecycle-commands';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';

export type SuggestEditParams = {
	/**
	 * The text content that is annotated (highlighted) in the page
	 */
	annotatedText?: string;
	/**
	 * The plain text content of the comment explaining what needs to be fixed
	 */
	commentText: string;
	/**
	 * The content ID of the page
	 */
	contentId?: string;
};

export type SuggestEditState = {
	/**
	 * Whether the suggest edit request is currently being processed
	 */
	isLoading: boolean;
	/**
	 * Error message if the request failed
	 */
	error: string | null;
	/**
	 * Whether the request was successful (edit was applied)
	 */
	isSuccess: boolean;
};

export type UseSuggestEditReturn = SuggestEditState & {
	/**
	 * Function to trigger the suggest edit request
	 */
	suggestEdit: (params: SuggestEditParams) => void;
	/**
	 * Reset the state back to initial values
	 */
	reset: () => void;
};

/**
 * Extracts the plain text content from comment ADF.
 *
 * Uses @atlaskit/adf-utils traverse to properly handle all ADF node types
 * including nested structures like lists, tables, and blockquotes.
 */
export const getCommentTextFromAdf = (commentAdf?: string): string => {
	if (!commentAdf) {
		return '';
	}
	try {
		const doc: DocNode = JSON.parse(commentAdf);
		const result: string[] = [];

		traverse(doc, {
			text: (node) => {
				result.push(node.text || '');
			},
			mention: (node) => {
				result.push(node.attrs?.text || '');
			},
			emoji: (node) => {
				result.push(node.attrs?.text || node.attrs?.shortName || '');
			},
			hardBreak: () => {
				result.push(' ');
			},
		});

		return result.join('').trim();
	} catch {
		return '';
	}
};

const initialState: SuggestEditState = {
	isLoading: false,
	error: null,
	isSuccess: false,
};

export type UseSuggestEditOptions = {
	/**
	 * The content ID of the page (required to listen for streaming events)
	 */
	contentId?: string;
	/**
	 * Whether the feature is enabled. When false, the hook will not register
	 * with the bridge API. This should be set based on experiment and feature
	 * gate conditions.
	 */
	enabled?: boolean;
};

export const useSuggestEdit = ({
	contentId,
	enabled = true,
}: UseSuggestEditOptions = {}): UseSuggestEditReturn => {
	const [state, setState] = useState<SuggestEditState>(initialState);
	const isActiveRef = useRef(false);
	const contentIdRef = useRef<string | undefined>(contentId);

	// Keep contentIdRef in sync
	useEffect(() => {
		contentIdRef.current = contentId;
	}, [contentId]);

	const reset = useCallback(() => {
		setState(initialState);
		isActiveRef.current = false;
	}, []);

	// Use a separate client ID for suggest-edit to avoid conflicting with the editor's bridge registration.
	// The editor registers with getConfluenceEditorClientId(contentId) and handles ContentReadyCommand
	// and ReadContentCommand. We just need to send messages and listen for stream events.
	const suggestEditClientId = client(`confluence-suggest-edit-${contentId || 'fallback'}`);

	// Set up bridge API with handlers for streaming lifecycle events
	const { send, ready } = useRovoContentBridgeAPI(suggestEditClientId, (on) => {
		// Handle stream completed - edit was successfully applied
		on(RovoStreamCompletedCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: null,
					isSuccess: true,
				});
				isActiveRef.current = false;
			}
		});

		// Handle stream error - edit failed
		on(RovoStreamErroredCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: 'Failed to apply edit',
					isSuccess: false,
				});
				isActiveRef.current = false;
			}
		});

		// Handle stream aborted - user cancelled
		on(RovoStreamAbortedCommand, () => {
			if (isActiveRef.current) {
				setState({
					isLoading: false,
					error: null,
					isSuccess: false,
				});
				isActiveRef.current = false;
			}
		});
	});

	// Mark the client as ready to receive messages just-in-time.
	// This avoids chaining state updates via an effect.
	const hasCalledReadyRef = useRef(false);

	const suggestEdit = useCallback(
		({ annotatedText, commentText, contentId: paramContentId }: SuggestEditParams) => {
			if (enabled && !hasCalledReadyRef.current) {
				ready();
				hasCalledReadyRef.current = true;
			}
			const effectiveContentId = paramContentId || contentId;
			contentIdRef.current = effectiveContentId;

			// Validate required context - contentId is essential for page context
			if (!effectiveContentId) {
				setState({
					isLoading: false,
					error: 'Missing page context. Unable to suggest edit.',
					isSuccess: false,
				});
				return;
			}

			if (!annotatedText) {
				setState({
					isLoading: false,
					error: 'No annotated text found. Unable to suggest edit.',
					isSuccess: false,
				});
				return;
			}

			// Set loading state
			setState({
				isLoading: true,
				error: null,
				isSuccess: false,
			});

			// Track this request
			isActiveRef.current = true;

			// Build the initial prompt with context about the comment
			const prompt =
				`Update the document using the ConfluenceCreationTool and fix the content "${annotatedText}"` +
				`based on the comment: "${commentText}".` +
				`Only fix the content. ` +
				`Do not provide your own comment.` +
				`Do not open up the Rovo chat window. openChat=false.`;

			try {
				send(InlineInputBoxSendMessageCommand, {
					receiverId: ROVO_CHAT_CLIENT_ID,
					prompt,
					source: 'CONFLUENCE_COMMENT_FIX',
					productKey: 'confluence',
					deepResearchEnabled: false,
					webSearchEnabled: false,
					contentId: effectiveContentId,
					openChat: false,
					addCurrentPageAsContextUserPreference: true,
				});
			} catch (error) {
				setState({
					isLoading: false,
					error: error instanceof Error ? error.message : 'Failed to send message to Rovo',
					isSuccess: false,
				});
				isActiveRef.current = false;
			}
		},
		[send, contentId, enabled, ready],
	);

	return {
		...state,
		suggestEdit,
		reset,
	};
};
